// utils/schoolTerm.js

/**
 * 获取春节日期（简化版，基于2020-2030年数据）
 */
const getSpringFestival = (year) => {
  const springFestivalDates = {
    2020: '2020-01-25',
    2021: '2021-02-12',
    2022: '2022-02-01',
    2023: '2023-01-22',
    2024: '2024-02-10',
    2025: '2025-01-29',
    2026: '2026-02-17',
    2027: '2027-02-06',
    2028: '2028-01-26',
    2029: '2029-02-13',
    2030: '2030-02-03'
  };

  return springFestivalDates[year] || `${year}-01-01`;
};

/**
 * 格式化日期为YYYY-MM-DD
 */
const formatDate = (date) => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

/**
 * 比较两个日期（YYYY-MM-DD格式）
 */
const compareDates = (dateStr1, dateStr2) => {
  if (dateStr1 === dateStr2) return 0;
  return dateStr1 > dateStr2 ? 1 : -1;
};

/**
 * 根据当前时间获取学校学期时间（主要方法）
 */
export const getSchoolTerm = (currentDate = new Date()) => {
  const currentYear = currentDate.getFullYear();
  const currentMonth = currentDate.getMonth() + 1;
  const currentDay = currentDate.getDate();

  // 格式化当前日期
  const currentDateStr = formatDate(currentDate);

  // 获取春节日期
  const springFestivalThisYear = getSpringFestival(currentYear);
  const springFestivalNextYear = getSpringFestival(currentYear + 1);
  const springFestivalLastYear = getSpringFestival(currentYear - 1);

  // 将日期字符串转换为可以比较的数字
  const formatForCompare = (str) => str.replace(/-/g, '');

  const currentNum = parseInt(formatForCompare(currentDateStr));
  const springThisYearNum = parseInt(formatForCompare(springFestivalThisYear));
  const springNextYearNum = parseInt(formatForCompare(springFestivalNextYear));
  const jul30Num = parseInt(formatForCompare(`${currentYear}-07-30`));
  const sep1Num = parseInt(formatForCompare(`${currentYear}-09-01`));
  const jul31Num = parseInt(formatForCompare(`${currentYear}-07-31`));
  const aug31Num = parseInt(formatForCompare(`${currentYear}-08-31`));

  // 判断逻辑
  if (currentNum >= springThisYearNum && currentNum <= jul30Num) {
    // 第一学期：春节当天到7月30日
    return {
      startDate: springFestivalThisYear,
      endDate: `${currentYear}-07-30`,
      termType: '第一学期',
      termName: `${currentYear}学年第一学期`,
      isCurrentTerm: true
    };
  } else if (currentNum >= sep1Num && currentNum < springNextYearNum) {
    // 第二学期：9月1日到明年春节前一天
    return {
      startDate: `${currentYear}-09-01`,
      endDate: springFestivalNextYear,
      termType: '第二学期',
      termName: `${currentYear}学年第二学期`,
      isCurrentTerm: true
    };
  } else if (currentNum >= jul31Num && currentNum <= aug31Num) {
    // 暑假期间
    return {
      startDate: `${currentYear}-09-01`,
      endDate: springFestivalNextYear,
      termType: '第二学期（即将开始）',
      termName: `${currentYear}学年第二学期`,
      isCurrentTerm: false,
      isHoliday: true
    };
  } else if (currentNum < springThisYearNum) {
    // 春节前：属于去年的第二学期
    return {
      startDate: `${currentYear - 1}-09-01`,
      endDate: springFestivalThisYear,
      termType: '第二学期（上学期延续）',
      termName: `${currentYear - 1}学年第二学期`,
      isCurrentTerm: true
    };
  }

  // 默认情况
  return {
    startDate: `${currentYear}-09-01`,
    endDate: springFestivalNextYear,
    termType: '默认第二学期',
    termName: `${currentYear}学年第二学期`,
    isCurrentTerm: true
  };
};

/**
 * 获取下一个学期
 */
export const getNextTerm = (currentDate = new Date()) => {
  const currentTerm = getSchoolTerm(currentDate);
  const currentYear = currentDate.getFullYear();

  if (currentTerm.termType.includes('第一学期')) {
    // 如果当前是第一学期，下一个是第二学期
    return {
      startDate: `${currentYear}-09-01`,
      endDate: getSpringFestival(currentYear + 1),
      termType: '第二学期',
      termName: `${currentYear}学年第二学期`
    };
  } else {
    // 如果当前是第二学期，下一个是明年的第一学期
    return {
      startDate: getSpringFestival(currentYear + 1),
      endDate: `${currentYear + 1}-07-30`,
      termType: '第一学期',
      termName: `${currentYear + 1}学年第一学期`
    };
  }
};

/**
 * 获取当前学年
 */
export const getCurrentSchoolYear = (currentDate = new Date()) => {
  const currentYear = currentDate.getFullYear();
  const currentMonth = currentDate.getMonth() + 1;

  if (currentMonth >= 9) {
    return `${currentYear}-${currentYear + 1}`;
  } else {
    return `${currentYear - 1}-${currentYear}`;
  }
};

/**
 * 获取指定年份的所有学期
 */
export const getYearTerms = (year) => {
  const springFestival = getSpringFestival(year);
  const nextSpringFestival = getSpringFestival(year + 1);

  return [
    {
      startDate: springFestival,
      endDate: `${year}-07-30`,
      termType: '第一学期',
      termName: `${year}学年第一学期`
    },
    {
      startDate: `${year}-09-01`,
      endDate: nextSpringFestival,
      termType: '第二学期',
      termName: `${year}学年第二学期`
    }
  ];
};

// 导出所有方法
export default {
  getSchoolTerm,
  getNextTerm,
  getCurrentSchoolYear,
  getYearTerms,
  getSpringFestival
};